/** The user header's Actions menu (plan "Actions menu"): the one-step actions on a person,
 *  each present only with its permission and for the kinds it applies to, and — except
 *  Diagnose, which only opens a tab — disabled with the reason while the draft is dirty (a
 *  one-step action saves at once, outside the draft).
 *
 *  - **Sign-in** — Send password reset… · Set password… (console_user_password; system and
 *    staff) · Revoke all sessions (console_user_revoke_tokens) · Disable MFA (an enrolled
 *    system / staff user) · Re-issue invite (a system user; console_user_invite).
 *  - **Photo** — Change photo… (the avatar is outside the changeset: its own PATCH).
 *  - **Investigate** — Diagnose with AI (console_entity_ai): the Diagnose tab.
 *  - **Danger** (Signal Red) — Soft-delete (then the users grid) / Restore · Anonymize…
 *    (type the email; a reason, O3).
 *
 *  `host` renders the open dialog or confirmation. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import { userRecordKeys } from '@/api/userRecord';
import * as usersApi from '@/api/users';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import type { RecordActionGroup, RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { usePermissions } from '@/hooks/usePermissions';

import { gridKindOf } from '../userLinks';

import { AnonymizeDialog } from './actions/AnonymizeDialog';
import { AvatarDialog } from './actions/AvatarDialog';
import { SendResetDialog, SetPasswordDialog } from './actions/PasswordDialogs';

type Open =
  | 'reset'
  | 'password'
  | 'revoke'
  | 'mfa'
  | 'invite'
  | 'photo'
  | 'delete'
  | 'restore'
  | 'anonymize'
  | null;

interface Confirmable {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  run: () => Promise<string>;
}

export function useUserImmediateActions({
  user,
  dirtyCount,
  onDiagnose,
}: {
  user: usersApi.ConsoleUserDetail;
  /** Unsaved changes in the draft: the one-step actions wait for them. */
  dirtyCount: number;
  /** Open the Diagnose tab (absent: the operator does not see it). */
  onDiagnose?: (() => void) | undefined;
}): { groups: RecordActionGroup[]; host: ReactNode } {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<Open>(null);
  const close = useCallback(() => setOpen(null), []);

  const refresh = useCallback(() => {
    // Prefix match: the detail, the users grids — and the edit context (deleted, baselines).
    void qc.invalidateQueries({ queryKey: ['consoleUsers'] });
    void qc.invalidateQueries({ queryKey: userRecordKeys.editContext(user.id) });
  }, [qc, user.id]);
  const done = useCallback(
    (message: string) => {
      setOpen(null);
      snackbar.show(message, 'success');
      refresh();
    },
    [snackbar, refresh],
  );

  const confirmRun = useMutation({
    mutationFn: (c: Confirmable) => c.run(),
    onSuccess: (message) => done(message),
    onError: (e: unknown) => {
      setOpen(null);
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'The action failed.', 'error');
    },
  });

  const waiting =
    dirtyCount > 0
      ? `Save or discard the ${dirtyCount} unsaved ${dirtyCount === 1 ? 'change' : 'changes'} first`
      : undefined;
  const item = (key: Exclude<Open, null>, label: string, extra: Partial<RecordActionItem> = {}): RecordActionItem => ({
    key,
    label,
    onSelect: () => setOpen(key),
    ...(waiting ? { disabled: true, description: waiting } : {}),
    ...extra,
  });

  const loginKind = user.kind !== 'guest';
  const signIn: RecordActionItem[] = [
    ...(loginKind && hasPerm('console_user_password')
      ? [item('reset', 'Send password reset…'), item('password', 'Set password…')]
      : []),
    ...(hasPerm('console_user_revoke_tokens') ? [item('revoke', 'Revoke all sessions')] : []),
    ...(loginKind && user.mfa_enrolled && hasPerm('console_user_manage') ? [item('mfa', 'Disable MFA')] : []),
    ...(user.kind === 'system' && hasPerm('console_user_invite') ? [item('invite', 'Re-issue invite')] : []),
  ];
  const manage = hasPerm('console_user_manage');
  const photo: RecordActionItem[] = manage ? [item('photo', 'Change photo…')] : [];
  const investigate: RecordActionItem[] =
    onDiagnose && hasPerm('console_entity_ai') ? [{ key: 'diagnose', label: 'Diagnose with AI', onSelect: onDiagnose }] : [];
  const danger: RecordActionItem[] = manage
    ? [
        user.is_deleted ? item('restore', 'Restore') : item('delete', 'Soft-delete…', { danger: true }),
        item('anonymize', 'Anonymize…', { danger: true }),
      ]
    : [];

  const confirmations: Partial<Record<Exclude<Open, null>, Confirmable>> = {
    revoke: {
      title: 'Revoke all sessions?',
      message: 'Signs the user out everywhere: every console and app token is revoked.',
      confirmLabel: 'Revoke all',
      destructive: true,
      run: async () => {
        const r = await usersApi.revokeTokens(user.id);
        return `Revoked ${r.revoked} ${r.revoked === 1 ? 'session' : 'sessions'}.`;
      },
    },
    mfa: {
      title: 'Disable MFA?',
      // The reset revokes the sessions of the user's one sign-in scope (dashboard MFA plan §3.8):
      // a staff user's dashboard sessions, a system user's console sessions.
      message:
        user.kind === 'staff'
          ? 'Removes every MFA device and recovery code of the user and signs them out of the dashboard (their dashboard sessions are revoked) — the recovery path for a lost authenticator.'
          : 'Removes every MFA device and recovery code of the user and revokes their console sessions — the recovery path for a lost authenticator.',
      confirmLabel: 'Disable MFA',
      destructive: true,
      run: async () => {
        const r = await usersApi.disableMfa(user.id);
        return `MFA disabled — ${r.tokens_revoked} ${r.tokens_revoked === 1 ? 'session' : 'sessions'} revoked.`;
      },
    },
    invite: {
      title: 'Re-issue the console invite?',
      message: 'Revokes any unused invite for this user and emails a fresh link.',
      confirmLabel: 'Re-issue',
      run: async () => {
        await usersApi.batch('resend_invite', [user.id]);
        return 'Invite re-issue queued. Prior invites for this user are revoked.';
      },
    },
    restore: {
      title: 'Restore user?',
      message: 'Clears the deleted flag and reactivates the account.',
      confirmLabel: 'Restore',
      run: async () => {
        await usersApi.restore(user.id);
        return 'User restored.';
      },
    },
  };

  const softDelete = useMutation({
    mutationFn: () => usersApi.softDelete(user.id),
    onSuccess: () => {
      setOpen(null);
      snackbar.show('User soft-deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['consoleUsers'] });
      // The grid this user belongs to — not history.back(), which leaves the console when
      // the page was opened in a new tab.
      navigate(`/users/${gridKindOf(user.kind)}`);
    },
    onError: (e: unknown) => {
      setOpen(null);
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Soft-delete failed.', 'error');
    },
  });

  let host: ReactNode = null;
  const confirmable = open ? confirmations[open] : undefined;
  if (confirmable) {
    host = (
      <ConfirmDialog
        open
        title={confirmable.title}
        message={confirmable.message}
        confirmLabel={confirmable.confirmLabel}
        destructive={confirmable.destructive ?? false}
        onConfirm={() => confirmRun.mutate(confirmable)}
        onCancel={close}
      />
    );
  } else if (open === 'delete') {
    host = (
      <ConfirmDialog
        open
        title="Soft-delete user?"
        message="Marks the user deleted, deactivates them and revokes every session. Reversible: Restore brings the account back."
        confirmLabel="Soft-delete"
        destructive
        onConfirm={() => softDelete.mutate()}
        onCancel={close}
      />
    );
  } else if (open === 'reset') {
    host = <SendResetDialog user={user} onClose={close} onDone={done} />;
  } else if (open === 'password') {
    host = <SetPasswordDialog user={user} onClose={close} onDone={done} />;
  } else if (open === 'photo') {
    host = <AvatarDialog user={user} onClose={close} onSaved={done} />;
  } else if (open === 'anonymize') {
    host = <AnonymizeDialog user={user} onClose={close} onDone={(email) => done(`User anonymized — now ${email}.`)} />;
  }

  return {
    groups: [
      { key: 'signin', label: 'Sign-in', items: signIn },
      { key: 'photo', label: 'Photo', items: photo },
      { key: 'investigate', label: 'Investigate', items: investigate },
      { key: 'danger', label: 'Danger', items: danger },
    ],
    host,
  };
}
